import { useEffect, useState } from "react";
import { AlertMessage, PageBanner, Spinner } from "@/components/reusable";
import {
  buildPipelineBoardModel,
  PIPELINE_STAGES,
  type PipelineBoardModel,
  type PipelineFileDqxState,
  type UploadingPipelineFileModel,
} from "@/lib/pipelineBoard";
import { getPipelineBulkActionTargets } from "@/lib/pipelineBulkActions";
import type { BatchExtractionStatus, BatchExtractionStatusCategory } from "@/services/api";
import { fetchPlatformUsers } from "@/services/meApi";
import { fetchProvidersCached } from "@/services/providersApi";
import { PipelineBoardToolbar, type PipelineBoardAdminFilters } from "./PipelineBoardToolbar";
import { PipelineColumn, type PipelineColumnSelection } from "./PipelineColumn";
import { PipelineWorkflowProgressBanner } from "./PipelineWorkflowProgressBanner";

import type { PipelineFileMeta } from "@/lib/pipelineTypes";

export type { PipelineFileMeta };

interface PipelineBoardProps {
  batchStatus: BatchExtractionStatus | null;
  fileMeta: PipelineFileMeta[];
  schemaNameByFileId: Record<string, string>;
  mappedFileIds: string[];
  structureConfirmedFileIds: string[];
  previewingFileId: string | null;
  pendingActionByFileId?: Record<string, "mapping" | "dqx" | "confirm" | "download">;
  rowCountByFileId?: Record<string, number>;
  tableCountByFileId?: Record<string, number>;
  globalConfidenceByFileId?: Record<string, number>;
  requiresReviewByFileId?: Record<string, boolean>;
  dqxStateByFileId?: Record<string, PipelineFileDqxState>;
  uploadingFiles?: UploadingPipelineFileModel[];
  validateSelection?: PipelineColumnSelection;
  confirmSelection?: PipelineColumnSelection;
  error?: string;
  infoMessage?: string;
  onDismissError?: () => void;
  singleFile?: {
    fileId: string;
    fileName: string;
    category: BatchExtractionStatusCategory;
    stateMessage: string | null;
    isExtracting: boolean;
    providerId?: string | null;
    uploadedBy?: string | null;
  } | null;
  /** Current user — used to resolve avatar letter for own uploads. */
  currentUser?: { userId: string; userEmail: string } | null;
  onAddFiles: () => void;
  onConfirmStructure: (fileId: string) => void;
  onContinueMapping: (fileId: string) => void;
  onPreview?: (fileId: string) => void;
  onRemoveFile?: (fileId: string) => void;
  onRunDataQuality?: (fileId: string) => void;
  onViewDqxResults?: (fileId: string) => void;
  onDownloadMapped?: (fileId: string) => void | Promise<void>;
  onRetryFailures?: () => void;
  bulkProgress?: { label: string } | null;
  onCancelBatch?: () => void;
  isPollingBatch?: boolean;
  /** True while files are uploading — locks Add only (not during batch ingest poll). */
  isUploading?: boolean;
  /** True while board-state is being fetched — shows a spinner instead of the empty state. */
  isRestoringBoard?: boolean;
  isTriggeringIngestion?: boolean;
  workflowFileCount?: number;
  uploadedWorkflowFileCount?: number;
  workflowBatchStatus?: BatchExtractionStatus | null;
  singleFileStatusLabel?: string | null;
  /** Board scope filters: provider for admin / multi-provider users; user filter admin-only. */
  adminFilters?: PipelineBoardAdminFilters | null;
}

export function PipelineBoard({
  batchStatus,
  fileMeta,
  schemaNameByFileId,
  mappedFileIds,
  structureConfirmedFileIds,
  previewingFileId,
  pendingActionByFileId,
  rowCountByFileId,
  tableCountByFileId,
  globalConfidenceByFileId,
  requiresReviewByFileId,
  error,
  infoMessage,
  onDismissError,
  isUploading = false,
  isRestoringBoard = false,
  singleFile,
  currentUser = null,
  onAddFiles,
  onConfirmStructure,
  onContinueMapping,
  onPreview,
  onRemoveFile,
  onRunDataQuality,
  onViewDqxResults,
  onDownloadMapped,
  onRetryFailures,
  bulkProgress = null,
  onCancelBatch,
  isPollingBatch = false,
  isTriggeringIngestion = false,
  workflowFileCount = 0,
  uploadedWorkflowFileCount = 0,
  workflowBatchStatus = null,
  singleFileStatusLabel,
  dqxStateByFileId,
  uploadingFiles,
  validateSelection,
  confirmSelection,
  adminFilters = null,
}: PipelineBoardProps) {
  const [providerNameById, setProviderNameById] = useState<Record<string, string>>({});
  const [emailByUserId, setEmailByUserId] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    fetchProvidersCached()
      .then((res) => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const p of res.items) {
          next[p.provider_id] = p.provider_name || p.provider_short_code || p.provider_id;
        }
        setProviderNameById(next);
      })
      .catch(() => {
        if (!cancelled) setProviderNameById({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchPlatformUsers()
      .then((users) => {
        if (cancelled) return;
        const next: Record<string, string> = {};
        for (const u of users) {
          const id = u.user_id?.trim();
          const email = u.email?.trim();
          if (id && email) next[id] = email;
        }
        setEmailByUserId(next);
      })
      .catch(() => {
        if (!cancelled) setEmailByUserId({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const model: PipelineBoardModel = buildPipelineBoardModel({
    batchStatus,
    fileMeta,
    schemaNameByFileId,
    providerNameById,
    emailByUserId,
    currentUserId: currentUser?.userId ?? null,
    currentUserEmail: currentUser?.userEmail ?? null,
    mappedFileIds,
    structureConfirmedFileIds,
    previewingFileId,
    rowCountByFileId,
    tableCountByFileId,
    globalConfidenceByFileId,
    requiresReviewByFileId,
    dqxStateByFileId,
    uploadingFiles,
    singleFile,
  });
  const bulkTargets = getPipelineBulkActionTargets(model.cards);

  const cardsByStage = PIPELINE_STAGES.reduce(
    (acc, stage) => {
      acc[stage.id] = model.cards.filter((card) => card.stage === stage.id);
      return acc;
    },
    {} as Record<(typeof PIPELINE_STAGES)[number]["id"], typeof model.cards>
  );

  const hasNoWork = model.totalFiles === 0 && !isUploading && !isTriggeringIngestion;
  const showLoadingState = hasNoWork && isRestoringBoard;
  const showEmptyState = hasNoWork && !isRestoringBoard && !isPollingBatch;

  const showWorkflowBanner =
    isUploading || isTriggeringIngestion || (isPollingBatch && workflowBatchStatus != null);

  return (
    <div className="flex min-h-[calc(100vh-4rem)] w-full flex-col bg-[var(--color-bg-secondary)]">
      <div className="mx-auto w-full max-w-7xl px-6 pt-8">
        <PageBanner
          title="Upload, Map, Process Files"
          subtitle="File ingestion workspace"
          className="!mb-8 !h-30"
        />
      </div>

      <PipelineBoardToolbar
        onAddFiles={onAddFiles}
        isUploading={isUploading}
        retryFailureCount={bulkTargets.retryFailureCount}
        onRetryFailures={onRetryFailures}
        bulkProgress={bulkProgress}
        adminFilters={adminFilters}
      />

      {error ? (
        <div className="px-3 pt-2 md:px-4">
          <AlertMessage variant="error" message={error} onDismiss={onDismissError} />
        </div>
      ) : null}

      {infoMessage ? (
        <div className="px-3 pt-2 md:px-4">
          <AlertMessage variant="info" message={infoMessage} />
        </div>
      ) : null}

      {showWorkflowBanner ? (
        <PipelineWorkflowProgressBanner
          isUploading={isUploading}
          isTriggeringIngestion={isTriggeringIngestion}
          isPollingBatch={isPollingBatch}
          workflowBatchStatus={workflowBatchStatus}
          workflowFileCount={workflowFileCount}
          uploadedFileCount={uploadedWorkflowFileCount}
          singleFileStatusLabel={singleFileStatusLabel}
          onCancelBatch={onCancelBatch}
        />
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col px-2 py-2 md:px-3">
        {showLoadingState ? (
          <div
            className="flex min-h-[320px] flex-1 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-primary)] px-5 py-10 text-center"
            data-testid="pipeline-board-loading"
          >
            <Spinner size="lg" />
            <p className="text-sm text-[var(--color-text-secondary)]">Loading your workspace…</p>
          </div>
        ) : showEmptyState ? (
          <div
            className="flex min-h-[320px] flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-primary)] px-5 py-10 text-center"
            data-testid="pipeline-board-empty"
          >
            <p className="mb-1 text-sm font-semibold text-[var(--color-text-primary)]">
              No files in the workspace yet
            </p>
            <p className="mb-3 max-w-md text-xs text-[var(--color-text-secondary)]">
              Add one or more files and choose a target schema to start ingesting files in the
              workspace.
            </p>
            <button
              type="button"
              className="rounded-md bg-[var(--color-button-primary-bg)] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[var(--color-button-primary-hover)]"
              onClick={onAddFiles}
            >
              Add files
            </button>
          </div>
        ) : (
          <div
            className="grid h-full min-h-[420px] w-full grid-cols-5 gap-2 [&>*]:min-w-0"
            data-testid="pipeline-board"
          >
            {PIPELINE_STAGES.map((stage) => (
              <PipelineColumn
                key={stage.id}
                stageId={stage.id}
                cards={cardsByStage[stage.id]}
                pendingActionByFileId={pendingActionByFileId}
                selection={
                  stage.id === "validate"
                    ? validateSelection
                    : stage.id === "upload"
                      ? confirmSelection
                      : undefined
                }
                onConfirmStructure={onConfirmStructure}
                onContinueMapping={onContinueMapping}
                onPreview={onPreview}
                onRemoveFile={onRemoveFile}
                onRunDataQuality={onRunDataQuality}
                onViewDqxResults={onViewDqxResults}
                onDownloadMapped={onDownloadMapped}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
